import React from "react";

import { EmptyState, Text } from "@performyard/py-components";
import { EmployeeSummary } from "@performyard/service-contracts";

import { StatCard } from "../components/StatCard";
import { CompetencyAxis, CompetencyRadar } from "./CompetencyRadar";

type CountTile = {
	key: "meets" | "exceeds" | "below";
	label: string;
};

const COUNT_TILES: ReadonlyArray<CountTile> = [
	{ key: "meets", label: "Meets Expectations" },
	{ key: "exceeds", label: "Exceeds Expectations" },
	{ key: "below", label: "Below Expectations" },
];

const MAX_AXES_PER_RADAR = 8;

// Split a group across as many radars as it needs, sharing the axes evenly rather than
// filling each chart to MAX_AXES_PER_RADAR. An even split keeps every chart plottable —
// CompetencyRadar degrades to a plain list under three axes, which a fill-first split
// would trigger whenever the remainder is one or two (17 axes would render 8/8/1).
const chunkAxes = (axes: CompetencyAxis[]): CompetencyAxis[][] => {
	if (axes.length === 0) {
		return [];
	}

	const radarCount = Math.ceil(axes.length / MAX_AXES_PER_RADAR);
	const baseSize = Math.floor(axes.length / radarCount);
	const remainder = axes.length % radarCount;

	const chunks: CompetencyAxis[][] = [];
	let start = 0;
	for (let index = 0; index < radarCount; index++) {
		const size = baseSize + (index < remainder ? 1 : 0);
		chunks.push(axes.slice(start, start + size));
		start += size;
	}
	return chunks;
};

export const CompetenciesSection: React.FC<{ summary: EmployeeSummary }> = ({ summary }) => {
	const competencies = summary.competencies;

	if (competencies === null) {
		return (
			<div className="mt-2" data-testid="competencies-empty">
				<EmptyState variant="illustration" option={2} label="Not enough competency data to summarize yet" />
			</div>
		);
	}

	const narrative = summary.section_narratives?.competencies ?? null;

	const companyCharts = chunkAxes(competencies.company_wide);
	const roleCharts = chunkAxes(competencies.role);
	// Two unsplit groups share a row — the only side-by-side case in the Figma. A lone or
	// split group takes the full width so its charts keep enough room for axis labels.
	const sideBySide = companyCharts.length === 1 && roleCharts.length === 1;

	return (
		<div className="mt-1 flex flex-col gap-2">
			<div className="flex gap-2" data-testid="competencies-counts">
				{COUNT_TILES.map((tile) => (
					<StatCard
						key={tile.key}
						label={tile.label}
						value={competencies.counts[tile.key]}
						testId={`competencies-count-${tile.key}`}
					/>
				))}
			</div>

			{narrative !== null ? (
				<Text size="paragraph-3" color="neutral-9" data-testid="competencies-narrative">
					{narrative}
				</Text>
			) : null}

			{companyCharts.length > 0 || roleCharts.length > 0 ? (
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="competencies-radars">
					{companyCharts.length > 0 ? (
						<CompetencyRadar title="Company Competency" charts={companyCharts} fullWidth={!sideBySide} />
					) : null}

					{roleCharts.length > 0 ? (
						<CompetencyRadar title="Role Competency" charts={roleCharts} fullWidth={!sideBySide} />
					) : null}
				</div>
			) : null}
		</div>
	);
};
